import React, { useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Alert
} from '@mui/material';
import { WelcomeBoardItem } from '../../../../common/interfaces';
import { WidgetConfig } from '../types';
import { WIDGET_DEFINITIONS } from '../constants';
import { formatGridPosition, wouldOverlap, getMaxSpan, parseGridTemplate } from '../utils';
import { GridPreview } from '../components/GridPreview';

interface WidgetConfigDialogProps {
    open: boolean;
    selectedWidget: WelcomeBoardItem | null;
    originalSelectedWidget: WelcomeBoardItem | null;
    widgetConfig: WidgetConfig;
    layout: any;
    gridColumns: number;
    gridRows: number;
    onClose: () => void;
    onWidgetChange: (widget: WelcomeBoardItem) => void;
    onConfigChange: (config: WidgetConfig) => void;
    onUpdate: (widget: WelcomeBoardItem) => void;
}

export const WidgetConfigDialog: React.FC<WidgetConfigDialogProps> = ({
    open,
    selectedWidget,
    originalSelectedWidget,
    widgetConfig,
    layout,
    gridColumns,
    gridRows,
    onClose,
    onWidgetChange,
    onConfigChange,
    onUpdate
}) => {
    const updateWidgetAndConfig = useCallback((newConfig: WidgetConfig) => {
        if (!selectedWidget) return;
        
        const updatedWidget = {
            ...selectedWidget,
            gridColumn: formatGridPosition(newConfig.colStart, newConfig.colSpan),
            gridRow: formatGridPosition(newConfig.rowStart, newConfig.rowSpan)
        };
        
        onConfigChange(newConfig);
        onWidgetChange(updatedWidget);
    }, [selectedWidget, onConfigChange, onWidgetChange]);

    const handleColStartChange = useCallback((newColStart: number) => {
        const maxColSpan = getMaxSpan(newColStart, gridColumns);
        const newColSpan = Math.min(widgetConfig.colSpan, maxColSpan);
        const newConfig = {
            ...widgetConfig,
            colStart: newColStart,
            colSpan: newColSpan
        };
        updateWidgetAndConfig(newConfig);
    }, [widgetConfig, gridColumns, updateWidgetAndConfig]);

    const handleRowStartChange = useCallback((newRowStart: number) => {
        const maxRowSpan = getMaxSpan(newRowStart, gridRows);
        const newRowSpan = Math.min(widgetConfig.rowSpan, maxRowSpan);
        const newConfig = {
            ...widgetConfig,
            rowStart: newRowStart,
            rowSpan: newRowSpan
        };
        updateWidgetAndConfig(newConfig);
    }, [widgetConfig, gridRows, updateWidgetAndConfig]);

    const handleColSpanChange = useCallback((newColSpan: number) => {
        const newConfig = {
            ...widgetConfig,
            colSpan: newColSpan
        };
        updateWidgetAndConfig(newConfig);
    }, [widgetConfig, updateWidgetAndConfig]);

    const handleRowSpanChange = useCallback((newRowSpan: number) => {
        const newConfig = {
            ...widgetConfig,
            rowSpan: newRowSpan
        };
        updateWidgetAndConfig(newConfig);
    }, [widgetConfig, updateWidgetAndConfig]);

    if (!selectedWidget) return null;

    const otherWidgets = layout.welcomeBoardItems.filter((w: WelcomeBoardItem) => w !== originalSelectedWidget);
    const hasOverlap = wouldOverlap(
        widgetConfig.colStart,
        widgetConfig.colSpan,
        widgetConfig.rowStart,
        widgetConfig.rowSpan,
        otherWidgets
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Configure Widget</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Position Controls */}
                    <Typography variant="h6" sx={{ mb: 2 }}>Position</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Column Start</InputLabel>
                            <Select
                                value={widgetConfig.colStart}
                                label="Column Start"
                                onChange={(e) => handleColStartChange(Number(e.target.value))}
                            >
                                {Array.from({ length: gridColumns }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Row Start</InputLabel>
                            <Select
                                value={widgetConfig.rowStart}
                                label="Row Start"
                                onChange={(e) => handleRowStartChange(Number(e.target.value))}
                            >
                                {Array.from({ length: gridRows }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    
                    {/* Size Controls */}
                    <Typography variant="h6" sx={{ mb: 2 }}>Size</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Column Span</InputLabel>
                            <Select
                                value={widgetConfig.colSpan}
                                label="Column Span"
                                onChange={(e) => handleColSpanChange(Number(e.target.value))}
                            >
                                {Array.from({ length: getMaxSpan(widgetConfig.colStart, gridColumns) }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Row Span</InputLabel>
                            <Select
                                value={widgetConfig.rowSpan}
                                label="Row Span"
                                onChange={(e) => handleRowSpanChange(Number(e.target.value))}
                            >
                                {Array.from({ length: getMaxSpan(widgetConfig.rowStart, gridRows) }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    
                    {/* Validation Feedback */}
                    {hasOverlap && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            This position would overlap with existing widgets
                        </Alert>
                    )}
                    
                    {/* Visual Grid Preview */}
                    <GridPreview
                        gridColumns={gridColumns}
                        gridRows={gridRows}
                        widgets={layout.welcomeBoardItems}
                        highlightWidget={widgetConfig}
                        excludeWidget={originalSelectedWidget}
                    />
                    
                    {/* Current CSS Grid Values */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        CSS Grid: Column {selectedWidget.gridColumn}, Row {selectedWidget.gridRow}
                    </Typography>
                    
                    {WIDGET_DEFINITIONS.find(def => def.type === selectedWidget.type)?.canHaveContent && (
                        <TextField
                            fullWidth
                            label="Content"
                            value={selectedWidget.content || ''}
                            onChange={(e) => onWidgetChange({
                                ...selectedWidget,
                                content: e.target.value
                            })}
                            multiline
                            rows={3}
                            helperText="Content specific to this widget type"
                            sx={{ mb: 2 }}
                        />
                    )}

                    <TextField
                        fullWidth
                        label="Custom CSS"
                        value={selectedWidget.css || ''}
                        onChange={(e) => onWidgetChange({
                            ...selectedWidget,
                            css: e.target.value
                        })}
                        multiline
                        rows={3}
                        helperText="Custom CSS styles for this widget (e.g., color: red; font-size: 16px;)"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => selectedWidget && onUpdate(selectedWidget)}
                >
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
};